import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from "react";
import { resolveAuthToken, clearImpersonationToken } from "../utils/resolveAuthToken";

interface PartnerMembership {
  partnerId: string;
  partnerSlug: string;
  partnerName: string;
  role: "admin" | "member";
}

interface ClientMembership {
  clientId: string;
  clientSlug: string;
  clientName: string;
  role: "admin" | "member";
  partnerId: string;
  appId: string;
}

interface User {
  id: string;
  email: string;
  name: string | null;
  status: "active" | "invited" | "deactivated";
  avatarUrl: string | null;
  userType: "internal" | "external";
  teamMemberId: string | null;
  teamId: string | null;
  teamSlug: string | null;
  teamName: string | null;
  memberships: Array<{
    projectId: string;
    projectName: string;
    projectSlug: string;
    role: "admin" | "editor" | "viewer";
  }>;
  partnerTenantIds?: string[];
  partnerMemberships: PartnerMembership[];
  isSuperAdmin: boolean;
  clientMemberships: ClientMembership[];
  /** Present only during an impersonation session. Server-verified — the
   * banner must never be driven by client storage, because a banner a script
   * can suppress is how an unmarked session happens. */
  impersonation?: {
    realEmail: string;
    targetEmail: string;
    expiresAt: string;
  };
}

interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  loginWithTokens: (accessToken: string, refreshToken: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshToken: () => Promise<boolean>;
  refreshProfile: () => Promise<void>;
  accessToken: string | null;
  /** Signal that a login flow is starting — makes ProtectedRoute show spinner */
  setLoggingIn: (v: boolean) => void;
  /** Inject user profile directly (e.g. from bootstrap response), bypassing fetchProfile */
  hydrateUser: (userData: User) => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const TOKEN_KEY = "cortex_access_token";
const REFRESH_KEY = "cortex_refresh_token";

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(() => localStorage.getItem(TOKEN_KEY));
  const [refreshTokenStr, setRefreshTokenStr] = useState<string | null>(() => localStorage.getItem(REFRESH_KEY));
  const [isLoading, setIsLoading] = useState(true);
  const [loggingIn, setLoggingIn] = useState(false);

  const clearAuth = useCallback(() => {
    setUser(null);
    setAccessToken(null);
    setRefreshTokenStr(null);
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  }, []);

  const refreshToken = useCallback(async (): Promise<boolean> => {
    const rt = refreshTokenStr || localStorage.getItem(REFRESH_KEY);
    if (!rt) return false;
    try {
      const res = await fetch("/api/auth/refresh", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) { clearAuth(); return false; }
      const data = await res.json();
      setAccessToken(data.access_token);
      localStorage.setItem(TOKEN_KEY, data.access_token);
      return true;
    } catch {
      clearAuth();
      return false;
    }
  }, [refreshTokenStr, clearAuth]);

  const fetchProfile = useCallback(async (token: string) => {
    // Under impersonation the profile must be the TARGET's: /api/me is what
    // drives the banner and every userType-keyed render in the app.
    const { token: effective, kind } = resolveAuthToken();
    const bearer = effective ?? token;
    try {
      const res = await fetch("/api/me", {
        headers: { Authorization: `Bearer ${bearer}` },
      });
      if (res.status === 401 && kind === "impersonation") {
        // The session lapsed or was stopped. Drop the impersonation token and
        // reload onto the operator's own session — never clearAuth(), which
        // would throw away the real tokens this tab still needs.
        clearImpersonationToken();
        window.location.reload();
        return;
      }
      if (res.status === 401) {
        const refreshed = await refreshToken();
        if (!refreshed) { clearAuth(); return; }
        const newToken = localStorage.getItem(TOKEN_KEY);
        if (!newToken) { clearAuth(); return; }
        const retry = await fetch("/api/me", {
          headers: { Authorization: `Bearer ${newToken}` },
        });
        if (!retry.ok) { clearAuth(); return; }
        setUser(await retry.json());
        // Fetch CloudFront signed cookies for media access
        fetch("/api/media/cookies", {
          credentials: "include",
          headers: { Authorization: `Bearer ${newToken}` },
        }).catch(() => {});
        return;
      }
      if (!res.ok) { clearAuth(); return; }
      setUser(await res.json());
      // Fetch CloudFront signed cookies for media access
      fetch("/api/media/cookies", {
        credentials: "include",
        headers: { Authorization: `Bearer ${bearer}` },
      }).catch(() => {});
    } catch {
      clearAuth();
    }
  }, [refreshToken, clearAuth]);

  useEffect(() => {
    // resolveAuthToken() rather than accessToken: a tab that is impersonating
    // must bootstrap from the impersonation token, or /api/me answers as the
    // operator and the banner never appears.
    const boot = resolveAuthToken().token ?? accessToken;
    if (boot) {
      fetchProfile(boot).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for token injection from PreviewContext (preview server login)
  useEffect(() => {
    const handler = () => {
      const token = localStorage.getItem(TOKEN_KEY);
      const rt = localStorage.getItem(REFRESH_KEY);
      if (token && !accessToken) {
        setAccessToken(token);
        setRefreshTokenStr(rt);
        fetchProfile(token);
      }
    };
    window.addEventListener("preview-tokens-injected", handler);
    return () => window.removeEventListener("preview-tokens-injected", handler);
  }, [accessToken, fetchProfile]);

  /** Accept pre-issued tokens (e.g. from the Atlas handoff) and load the profile.
   *
   * Throws if the profile could not be loaded. The caller needs to know:
   * fetchProfile clears the session on failure, so a silent return would send
   * the person back to the login page with no explanation, right after a
   * sign-in that actually succeeded.
   */
  const loginWithTokens = useCallback(async (at: string, rt: string) => {
    setAccessToken(at);
    setRefreshTokenStr(rt);
    localStorage.setItem(TOKEN_KEY, at);
    localStorage.setItem(REFRESH_KEY, rt);
    await fetchProfile(at);
    setIsLoading(false);
    setLoggingIn(false);
    if (!localStorage.getItem(TOKEN_KEY)) {
      throw new Error("Signed in, but your profile could not be loaded. Please try again.");
    }
  }, [fetchProfile]);

  const refreshProfile = useCallback(async () => {
    const token = resolveAuthToken().token ?? accessToken ?? localStorage.getItem(TOKEN_KEY);
    if (token) await fetchProfile(token);
  }, [accessToken, fetchProfile]);

  const logout = useCallback(async () => {
    const rt = refreshTokenStr || localStorage.getItem(REFRESH_KEY);
    if (rt) {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      }).catch(() => {});
    }
    clearAuth();
  }, [refreshTokenStr, clearAuth]);

  const hydrateUser = useCallback((userData: User) => {
    setUser(userData);
    setIsLoading(false);
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated: !!user,
      isLoading: isLoading || loggingIn,
      isLoggingIn: loggingIn,
      loginWithTokens,
      logout,
      refreshToken,
      refreshProfile,
      accessToken,
      setLoggingIn,
      hydrateUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
